// WHAT: Statistics overview component displaying Miracle Flow session and journal statistics
// WHY:  Provides comprehensive analytics dashboard for tracking user progress and engagement
// DOMAIN: Productivity → Miracle Flow → Analytics
// DEPENDENCIES: useDashboardStats hook, useDailySessionSummaries hook, SessionStats component
// RISKS:
//   - NOTE: Data filtering by period may not handle timezone edge cases correctly
//   - NOTE: Multiple data sources (stats + summaries) - may have sync issues
//   - WARN: Error handling displays generic message - may not help debug specific failures
//   - NOTE: Period selection state is local - may not persist across navigation

import { ChevronLeft, RefreshCw, AlertTriangle, BarChart3 } from "lucide-react";
import React, { useState } from "react";

import { Button, Badge, Card, Stack, Heading } from "@/components/ui";
import { cn } from "@/shared/utils";

import {
  useDailySessionSummaries,
  type DailySessionSummary,
} from "../../hooks/useDailySessionSummaries";
import { useDashboardStats } from "../../hooks/useDashboardStats";
import { type SessionStats as SessionStatsType, type JournalStats } from "../../types";
import { SessionStats as SessionStatsComponent } from "../molecules/SessionStats";

export interface StatsOverviewProperties {
  className?: string;
  onBack?: () => void;
}

/**
 * Filters summaries by selected time period.
 *
 * @param summaries - Daily summaries to filter
 *
 * @param period - Selected time period
 *
 * @returns Filtered summaries array
 *
 */
const filterSummariesByPeriod = (
  summaries: DailySessionSummary[],
  period: string,
): DailySessionSummary[] => {
  const now = new Date();

  switch (period) {
    case "today": {
      const today = now.toISOString().split("T")[0];
      return summaries.filter((summary) => summary.date === today);
    }
    case "week": {
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return summaries.filter((summary) => {
        const summaryDate = new Date(summary.date);
        return summaryDate >= weekAgo;
      });
    }
    case "month": {
      const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      return summaries.filter((summary) => {
        const summaryDate = new Date(summary.date);
        return summaryDate >= monthAgo;
      });
    }
    case "year": {
      const yearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
      return summaries.filter((summary) => {
        const summaryDate = new Date(summary.date);
        return summaryDate >= yearAgo;
      });
    }
    default: {
      return summaries;
    }
  }
};

/**
 * Calculate session counts from a summary
 *
 * @param summary - Daily session summary
 *
 * @returns Object with session counts
 *
 */
const calculateSessionCounts = (summary: DailySessionSummary) => {
  const hasMorning = summary.morning_session === undefined ? 0 : 1;
  const hasEvening = summary.evening_session === undefined ? 0 : 1;
  const hasSession = hasMorning + hasEvening;

  return {
    hasEvening,
    hasMorning,
    hasSession,
  };
};

/**
 * Calculate streak statistics from sorted summaries
 *
 * @param sortedSummaries - Summaries sorted by date descending
 *
 * @returns Object with streak statistics
 *
 */
const calculateStreaks = (sortedSummaries: DailySessionSummary[]) => {
  let tempStreak = 0;
  let maxStreak = 0;
  let lastDate: Date | null = null;

  for (const summary of sortedSummaries) {
    const summaryDate = new Date(summary.date);
    const { hasSession } = calculateSessionCounts(summary);

    if (hasSession > 0) {
      if (lastDate === null) {
        tempStreak = 1;
        maxStreak = 1;
      } else {
        const daysDiff = Math.floor(
          (lastDate.getTime() - summaryDate.getTime()) / (24 * 60 * 60 * 1000),
        );
        if (daysDiff === 1) {
          tempStreak += 1;
          maxStreak = Math.max(maxStreak, tempStreak);
        } else {
          tempStreak = 1;
        }
      }
      lastDate = summaryDate;
    }
  }

  return {
    currentStreak: tempStreak,
    longestStreak: maxStreak,
  };
};

/**
 * Count sessions in time periods
 *
 * @param summaries - Daily summaries
 *
 * @param weekAgo - Date one week ago
 *
 * @param monthAgo - Date one month ago
 *
 * @returns Object with session counts for time periods
 *
 */
const countSessionsInPeriods = (
  summaries: DailySessionSummary[],
  weekAgo: Date,
  monthAgo: Date,
) => {
  let sessionsThisWeek = 0;
  let sessionsThisMonth = 0;

  for (const summary of summaries) {
    const summaryDate = new Date(summary.date);
    const { hasSession } = calculateSessionCounts(summary);

    if (summaryDate >= weekAgo) {
      sessionsThisWeek += hasSession;
    }
    if (summaryDate >= monthAgo) {
      sessionsThisMonth += hasSession;
    }
  }

  return {
    sessionsThisMonth,
    sessionsThisWeek,
  };
};

/**
 * Calculates session statistics from filtered summaries.
 *
 * @param summaries - Filtered daily summaries
 *
 * @returns Session statistics object
 *
 */
const calculateSessionStats = (summaries: DailySessionSummary[]): SessionStatsType => {
  const now = new Date();
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

  // Sort summaries by date descending for streak calculation
  const sortedSummaries = summaries.toSorted((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return dateB.getTime() - dateA.getTime();
  });

  let totalSessions = 0;
  let completedSessions = 0;
  let morningSessions = 0;
  let eveningSessions = 0;
  let totalTimeMinutes = 0;

  for (const summary of sortedSummaries) {
    const { hasEvening, hasMorning, hasSession } = calculateSessionCounts(summary);

    totalSessions += hasSession;
    completedSessions += hasSession;
    morningSessions += hasMorning;
    eveningSessions += hasEvening;

    // Calculate total time (placeholder - actual duration calculation needed)
    // NOTE: This is a placeholder - actual duration should come from session data
    totalTimeMinutes += hasSession * 30; // Assuming 30 minutes per session as placeholder
  }

  const { currentStreak, longestStreak } = calculateStreaks(sortedSummaries);
  const { sessionsThisMonth, sessionsThisWeek } = countSessionsInPeriods(
    summaries,
    weekAgo,
    monthAgo,
  );

  const completionRate = totalSessions > 0 ? (completedSessions / totalSessions) * 100 : 0;
  const averageDuration =
    completedSessions > 0 ? Math.round(totalTimeMinutes / completedSessions) : 0;

  return {
    average_duration_minutes: averageDuration,
    completed_sessions: completedSessions,
    completion_rate: completionRate,
    current_streak: currentStreak,
    evening_sessions: eveningSessions,
    longest_streak: longestStreak,
    morning_sessions: morningSessions,
    sessions_this_month: sessionsThisMonth,
    sessions_this_week: sessionsThisWeek,
    total_sessions: totalSessions,
    total_time_minutes: totalTimeMinutes,
  };
};

/**
 * WHAT:
 *   Statistics overview component displaying Miracle Flow session and journal statistics.
 *
 * WHY:
 *   Provides comprehensive analytics dashboard for tracking user progress and engagement.
 *
 * PROPS:
 *   - onBack?: () => void — Optional callback for back navigation
 *   - className?: string — Optional CSS classes for styling
 *
 * BEHAVIOR:
 *   - Fetches dashboard stats and daily session summaries
 *   - Filters data based on selected period (today, week, month, year)
 *   - Displays session statistics and journal statistics
 *   - Provides period selection and refresh functionality
 *   - Shows loading and error states
 *
 * RISKS:
 *   - NOTE: Data filtering by period may not handle timezone edge cases correctly
 *   - NOTE: Multiple data sources (stats + summaries) - may have sync issues
 *   - WARN: Error handling displays generic message - may not help debug specific failures
 *   - NOTE: Period selection state is local - may not persist across navigation
 *
 * @param root0
 *
 * @param root0.onBack
 *
 * @param root0.className
 *
 * @returns JSX.Element
 *
 */
export const StatsOverview: React.FC<StatsOverviewProperties> = ({ className, onBack }) => {
  const [selectedPeriod, setSelectedPeriod] = useState<"month" | "today" | "week" | "year">(
    "month",
  );

  // Use real data hooks
  const {
    data: dashboardStats,
    error: statsError,
    isLoading: statsLoading,
    refetch: refetchStats,
  } = useDashboardStats();

  const {
    data: dailySummaries,
    error: summariesError,
    isLoading: summariesLoading,
    refetch: refetchSummaries,
  } = useDailySessionSummaries();

  const isLoading = statsLoading || summariesLoading;
  const error = statsError ?? summariesError;

  /**
   * WHAT:
   *   Reloads both dashboard stats and daily session summaries.
   *
   * WHY:
   *   Provides manual refresh functionality for up-to-date statistics.
   *
   * SIDE EFFECTS:
   *   - Triggers refetch for both data sources
   *   - Updates loading states
   *
   * RISKS:
   *   - NOTE: Both refetches run in parallel - may cause race conditions
   *   - NOTE: No error handling - failures may be silent
   *
   * @returns Promise that resolves when both refetches complete
   *
   */
  const loadStats = async (): Promise<void> => {
    await Promise.all([refetchStats(), refetchSummaries()]);
  };

  /**
   * WHAT:
   *   Filters daily summaries based on selected time period.
   *
   * WHY:
   *   Provides period-based data filtering for statistics display.
   *
   * OUTPUT:
   *   Object with filtered sessionStats and journalStats.
   *
   * RISKS:
   *   - NOTE: Date filtering uses local timezone - may not match user's timezone
   *   - NOTE: Period calculations use fixed day counts (7, 30, 365) - may not be accurate
   *   - WARN: Date string comparison may fail with different date formats
   *
   * @returns Object with filtered sessionStats and journalStats
   *
   */
  const getFilteredData = (): {
    journalStats: JournalStats | null;
    sessionStats: null | SessionStatsType;
  } => {
    if (!dailySummaries) {
      return { journalStats: null, sessionStats: null };
    }

    const filteredSummaries = filterSummariesByPeriod(dailySummaries, selectedPeriod);
    const sessionStats: SessionStatsType = calculateSessionStats(filteredSummaries);

    // Calculate journal stats (entries with content)
    let journalEntries = 0;
    for (const summary of filteredSummaries) {
      let entryCount = 0;

      if (summary.morning_session) {
        const hasScribing = summary.morning_session.inputs.some(
          (input) => input.step === "scribing" && input.value.trim().length > 0,
        );
        const hasReading = summary.morning_session.inputs.some(
          (input) => input.step === "reading" && input.value.trim().length > 0,
        );
        if (hasScribing || hasReading) {
          entryCount++;
        }
      }

      if (summary.evening_session) {
        const hasReflection = summary.evening_session.inputs.some(
          (input) => input.step === "evening_reflection" && input.value.trim().length > 0,
        );
        if (hasReflection) {
          entryCount++;
        }
      }

      journalEntries += entryCount;
    }

    const journalStats: JournalStats = {
      completion_rate:
        sessionStats.total_sessions > 0 ? (journalEntries / sessionStats.total_sessions) * 100 : 0,
      current_streak: dashboardStats?.current_streak ?? 0,
      entries_this_month: journalEntries,
      total_entries: journalEntries,
    };

    return { journalStats, sessionStats };
  };

  const { journalStats, sessionStats } = getFilteredData();

  const renderPeriodSelector = () => (
    <div className="flex gap-2 bg-[#0F172A] p-1 rounded-tracq border border-[#334155]">
      {(["today", "week", "month", "year"] as const).map((period) => (
        <Button
          className={cn(
            selectedPeriod === period
              ? "bg-gradient-to-r from-tracq-deep-blue to-tracq-bright-blue hover:from-tracq-deep-blue/90 hover:to-tracq-bright-blue/90 text-white"
              : "border-[#334155] text-[#CBD5E1] hover:bg-[#334155] hover:border-tracq-bright-blue/50",
          )}
          key={period}
          onClick={() => {
            setSelectedPeriod(period);
          }}
          size="sm"
          variant={selectedPeriod === period ? "primary" : "outline"}
        >
          {period.charAt(0).toUpperCase() + period.slice(1)}
        </Button>
      ))}
    </div>
  );

  if (isLoading) {
    return (
      <Card
        className={cn("bg-[#1E293B] border-[#334155]", className)}
        padding="lg"
        variant="default"
      >
        <Stack align="center" className="py-12" direction="col" gap={4}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tracq-bright-blue" />
          <p className="text-[#94A3B8]">Loading statistics...</p>
        </Stack>
      </Card>
    );
  }

  return (
    <div className={cn("space-y-6", className)}>
      {/* Header */}
      <Card className="bg-[#1E293B] border-[#334155]" padding="lg" variant="default">
        <Stack align="center" direction="row" justify="between">
          <Stack align="center" direction="row" gap={4}>
            {onBack ? (
              <Button
                className="border-[#334155] text-[#CBD5E1] hover:bg-[#334155] hover:border-tracq-bright-blue/50"
                onClick={onBack}
                size="sm"
                variant="outline"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            ) : null}

            <Stack direction="col" gap={1}>
              <Heading className="text-[#F1F5F9] font-bold" level="h2" size="xl">
                Statistics Overview
              </Heading>
              <p className="text-[#94A3B8]">Track your progress and celebrate your achievements</p>
            </Stack>
          </Stack>

          <Stack align="center" direction="row" gap={3}>
            {renderPeriodSelector()}
            <Button
              className="border-[#334155] text-[#CBD5E1] hover:bg-[#334155] hover:border-tracq-bright-blue/50"
              onClick={() => {
                void loadStats();
              }}
              variant="outline"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </Stack>
        </Stack>
      </Card>

      {/* Error Display */}
      {error ? (
        <Card className="bg-red-500/10 border-red-500/20" padding="md" variant="default">
          <Stack align="center" direction="row" gap={3}>
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <p className="text-red-300 text-sm">{String(error)}</p>
          </Stack>
        </Card>
      ) : null}

      {/* Period Badge */}
      <div className="flex justify-center">
        <Badge
          className="bg-tracq-bright-blue/20 text-tracq-bright-blue border-tracq-bright-blue/30 px-4 py-2"
          size="lg"
          variant="info"
        >
          <BarChart3 className="w-4 h-4 mr-2" />
          Statistics for{" "}
          {(() => {
            if (selectedPeriod === "today") {
              return "Today";
            }
            if (selectedPeriod === "week") {
              return "This Week";
            }
            if (selectedPeriod === "month") {
              return "This Month";
            }
            return "This Year";
          })()}
        </Badge>
      </div>

      {/* Main Stats */}
      {(() => {
        if (!sessionStats && !journalStats) {
          return null;
        }
        const period = selectedPeriod === "today" ? "week" : selectedPeriod;
        return (
          <SessionStatsComponent
            {...(journalStats ? { journalStats } : {})}
            period={period}
            {...(sessionStats ? { sessionStats } : {})}
            showDetailedView
          />
        );
      })()}

      {/* Empty State */}
      {/* NOTE: Empty state only shows when both stats are null (which happens when dailySummaries is null) */}
      {/* Reason: TypeScript narrows types - if dailySummaries exists, journalStats is always an object (never null) */}
      {}
      {!isLoading && sessionStats === null && journalStats === null ? (
        <Card className="bg-[#1E293B] border-[#334155]" padding="lg" variant="default">
          <Stack align="center" className="py-12" direction="col" gap={4}>
            <div className="flex items-center justify-center w-16 h-16 rounded-tracq bg-gradient-to-br from-tracq-deep-blue/20 to-tracq-bright-blue/20 border border-tracq-bright-blue/30">
              <BarChart3 className="w-8 h-8 text-tracq-bright-blue" />
            </div>
            <Stack align="center" direction="col" gap={2}>
              <Heading className="text-[#F1F5F9] text-center" level="h3" size="lg">
                No data available
              </Heading>
              <p className="text-[#94A3B8] text-center">
                Start your Miracle Flow sessions to see statistics here
              </p>
            </Stack>
          </Stack>
        </Card>
      ) : null}
    </div>
  );
};
